import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  updateObjektPhase,
  type ObjektState,
  type Phase,
} from "@/api/objekte";
import { PHASE_OPTIONS } from "@/components/PhaseBadge";
import { Button } from "@/components/ui/button";

interface Props {
  state: ObjektState;
}

/** Inline editor for `phase` + `notes` of a single objekt. */
export function PhaseEditor({ state }: Props) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>(state.phase);
  const [notes, setNotes] = useState<string>(state.notes ?? "");

  // Reset local state if the underlying objekt is refetched (e.g. after another
  // tab edits it). Keying off slug+last_updated so concurrent navigations work.
  useEffect(() => {
    setPhase(state.phase);
    setNotes(state.notes ?? "");
  }, [state.phase, state.notes, state.slug]);

  const dirty =
    phase !== state.phase || (notes ?? "") !== (state.notes ?? "");

  const mutation = useMutation({
    mutationFn: () =>
      updateObjektPhase(state.slug, phase, notes.trim() ? notes : null),
    onSuccess: () => {
      toast.success("Phase aktualisiert.");
      void qc.invalidateQueries({ queryKey: ["objekte"] });
      void qc.invalidateQueries({ queryKey: ["dossier", state.slug] });
    },
    onError: (err) => {
      const detail =
        err instanceof AxiosError
          ? (err.response?.data as { detail?: string } | undefined)?.detail ??
            err.message
          : err instanceof Error
            ? err.message
            : "Unbekannter Fehler";
      toast.error("Phase-Update fehlgeschlagen", { description: detail });
    },
  });

  return (
    <section
      className="rounded-lg border p-4 space-y-3"
      data-testid="phase-editor"
      aria-label="Phase und Notizen bearbeiten"
    >
      <h2 className="text-sm font-medium text-muted-foreground">Phase</h2>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="space-y-1 sm:col-span-1">
          <span className="text-xs font-medium">Status</span>
          <select
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={phase}
            onChange={(e) => setPhase(e.target.value as Phase)}
            data-testid="phase-select"
            aria-label="Phase"
          >
            {PHASE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1 sm:col-span-2">
          <span className="text-xs font-medium">Notizen</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="Optional — kurze Notiz zum aktuellen Stand."
            data-testid="phase-notes"
          />
        </label>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!dirty || mutation.isPending}
          data-testid="phase-save"
        >
          {mutation.isPending ? "Speichere …" : "Speichern"}
        </Button>
        {dirty && !mutation.isPending && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setPhase(state.phase);
              setNotes(state.notes ?? "");
            }}
          >
            Abbrechen
          </Button>
        )}
        {!dirty && (
          <span className="text-xs text-muted-foreground">
            Keine Änderungen.
          </span>
        )}
      </div>
    </section>
  );
}
